// Map a `CaseGraphEntityDetail` (API shape) into the `FullEntityDetail`
// expected by the modern-design entity-inspector primitives
// (`InvestigationScoreCard`, `EntityStatTriplet`, `EntityPropertiesList`, ...).
//
// Originally lived inline in `DocumentDetailOverlay.tsx`; moved here so the
// investigation sidebar can render the same inspector blocks for the same data.

import type { FullEntityDetail } from '@/components/document-overlay/EntityInspector'
import type { CaseGraphEntityDetail } from '@/services/api/cases'
import { isHiddenPropertyKey } from '@/utils/system-properties'

export function formatPropValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

export function mapEntityDetailToFullDetail(data: CaseGraphEntityDetail): FullEntityDetail {
  const c = data.score_components
  const factors =
    c == null
      ? []
      : ([
          { label: 'Evidence diversity', value: c.evidence_diversity ?? 0 },
          { label: 'Source diversity', value: c.source_diversity ?? 0 },
          { label: 'Target proximity', value: c.target_proximity ?? 0 },
          { label: 'Connections', value: c.connections ?? 0 },
        ] as { label: string; value: number }[])
  return {
    matchConfidence: data.match_confidence,
    investigationScore: data.investigation_score,
    investigationDescription:
      'Ranks the entity by investigative importance based on evidence diversity, source diversity, target proximity and connection patterns.',
    scoreFactors: factors,
    connections: data.connections,
    sources: data.sources,
    flaggedAsSuspect: data.is_suspect,
    properties: Object.entries(data.properties)
      .filter(([key]) => !isHiddenPropertyKey(key))
      .map(([key, value]) => ({
        key,
        value: formatPropValue(value),
      })),
  }
}
